import React, { useRef, useEffect, useState, useCallback } from 'react'
import * as THREE from 'three'
import { evaluateGameStats, subscribeAchievementUnlocked } from './achievements'
import { playWhooshSound, playPunchSound, playHitSound, playBellSound, playTrophySound, playBounceSound } from './audio'

export function PolarBoxing3D({ polar, demo, hrv, onExit }) {
  const mountRef = useRef(null)

  // Estados de React para el HUD
  const [gameState, setGameState] = useState('menu') // 'menu' | 'playing' | 'gameover'
  const [phase, setPhase] = useState('dodge') // 'dodge' (esquivar) | 'attack' (pegar)
  const [phaseTimer, setPhaseTimer] = useState(15)
  const [round, setRound] = useState(1)
  const [score, setScore] = useState(0)
  const [enemyHp, setEnemyHp] = useState(100)
  const [playerHp, setPlayerHp] = useState(100)
  const [slipsCount, setSlipsCount] = useState(0)
  const [punchesCount, setPunchesCount] = useState(0)
  const [hopsCount, setHopsCount] = useState(0)
  const [footworkPrompt, setFootworkPrompt] = useState(null)
  const [unlockedToast, setUnlockedToast] = useState(null)
  const [currentPose, setCurrentPose] = useState('center') // 'left' | 'right' | 'duck' | 'back' | 'center' | 'punch' | 'hop'
  const [lastStats, setLastStats] = useState(null)
  const [glError, setGlError] = useState(false) // el navegador/GPU no pudo abrir un contexto WebGL

  // Referencias mutables para el bucle Three.js a 60 FPS
  const stateRef = useRef({
    phase: 'dodge', // 'dodge' | 'attack'
    phaseTimer: 15,
    round: 1,
    score: 0,
    slips: 0,
    punches: 0,
    enemyHp: 100,
    playerHp: 100,
    playerPose: 'center', // 'left', 'right', 'duck', 'back', 'center', 'hop'
    attackPending: null, // Tipo de golpe que viene: 'left_hook', 'right_hook', 'jab', 'pull', 'footwork'
    attackProgress: 0,
    hasDodgedCurrent: false,
    hopsNeeded: 0,
    hopsDone: 0,
    lastHopTs: 0,
    totalHops: 0,
    currentHr: 72,
    isColdBlood: true, // BPM <= 115
    lastPunchTs: 0,
    prevAccZ: 0,
    cameraShake: 0,
    combo: 0,
    startTime: 0,
  })

  // Pulso real o simulado
  const liveHr = (polar && polar.status === 'connected' && polar.hr)
    ? polar.hr
    : (demo && demo.active && demo.hr)
      ? demo.hr
      : 72

  const isBleConnected = (polar && polar.status === 'connected') || (demo && demo.active)
  // Espejo en ref para leer el estado de conexión dentro del bucle 3D sin reconstruir el renderer.
  const connectedRef = useRef(isBleConnected)
  useEffect(() => { connectedRef.current = isBleConnected }, [isBleConnected])

  // ── Feedback visual ("juice"): textos de impacto, combo y flashes de pantalla ──
  const [combo, setCombo] = useState(0)
  const [fx, setFx] = useState(null)       // { id, text, sub, color }
  const [flash, setFlash] = useState(null) // { id, color }
  const fxTimerRef = useRef(null)
  const flashTimerRef = useRef(null)
  const triggerFx = useCallback((text, sub, color) => {
    setFx({ id: Date.now() + Math.random(), text, sub, color })
    clearTimeout(fxTimerRef.current)
    fxTimerRef.current = setTimeout(() => setFx(null), 720)
  }, [])
  const triggerFlash = useCallback((color) => {
    setFlash({ id: Date.now() + Math.random(), color })
    clearTimeout(flashTimerRef.current)
    flashTimerRef.current = setTimeout(() => setFlash(null), 170)
  }, [])
  const bumpCombo = useCallback(() => {
    const s = stateRef.current
    s.combo = (s.combo || 0) + 1
    setCombo(s.combo)
  }, [])
  const resetCombo = useCallback(() => {
    stateRef.current.combo = 0
    setCombo(0)
  }, [])
  useEffect(() => () => { clearTimeout(fxTimerRef.current); clearTimeout(flashTimerRef.current) }, [])

  // ── Detección Biomecánica Robusta con el Acelerómetro del Polar H10 (50 Hz) ──
  useEffect(() => {
    const s = stateRef.current
    s.currentHr = liveHr
    s.isColdBlood = liveHr <= 115

    const accYList = polar?.accSamples?.current?.y || []
    const accXList = polar?.accSamples?.current?.x || []
    const accZList = polar?.accSamples?.current?.z || []

    if (accYList.length > 0) {
      const curY = accYList[accYList.length - 1]
      const curX = accXList.length > 0 ? accXList[accXList.length - 1] : 0
      const curZ = accZList.length > 0 ? accZList[accZList.length - 1] : 0

      // Detección de golpe seco hacia adelante (impulso frontal en Eje Z)
      const jerkZ = Math.abs(curZ - s.prevAccZ)
      s.prevAccZ = curZ
      const now = Date.now()

      if (jerkZ > 52 && now - s.lastPunchTs > 380) {
        s.lastPunchTs = now
        executePlayerPunch()
        setCurrentPose('punch')
        setTimeout(() => setCurrentPose(s.playerPose), 250)
        return
      }

      // Detección de saltito vertical en punta de pie (rebote de footwork en Eje X > 275)
      if (curX > 275 && now - s.lastHopTs > 230) {
        s.lastHopTs = now
        registerHop()
        setCurrentPose('hop')
        setTimeout(() => setCurrentPose(s.playerPose), 220)
        return
      }

      // Detección de esquives de torso
      if (curY < -48) {
        s.playerPose = 'left'
        setCurrentPose('left')
      } else if (curY > 48) {
        s.playerPose = 'right'
        setCurrentPose('right')
      } else if (curZ > 85 || curX < 145) {
        s.playerPose = 'duck' // Agachado
        setCurrentPose('duck')
      } else if (curZ < -55) {
        s.playerPose = 'back' // Tirado atrás
        setCurrentPose('back')
      } else {
        s.playerPose = 'center'
        setCurrentPose('center')
      }
    }
  }, [liveHr, polar?.accSamples?.current?.x?.length, polar?.accSamples?.current?.y?.length, polar?.accSamples?.current?.z?.length])

  // Registro de saltitos / footwork activo para elevar el cardio
  const registerHop = useCallback(() => {
    const s = stateRef.current
    s.totalHops += 1
    playBounceSound()
    s.cameraShake = 0.12

    if (s.attackPending === 'footwork') {
      s.hopsDone += 1
      setHopsCount(s.hopsDone)
      s.score += 40
      setScore(s.score)
      if (s.hopsDone >= s.hopsNeeded) {
        s.hasDodgedCurrent = true
        s.slips += 1
        s.score += 150
        setSlipsCount(s.slips)
        setScore(s.score)
        setFootworkPrompt(null)
        bumpCombo()
        triggerFx('¡FOOTWORK!', '+150', '#30d158')
      }
    }
  }, [bumpCombo, triggerFx])

  // Ejecución de golpe del jugador durante la FASE DE ATAQUE
  const executePlayerPunch = useCallback(() => {
    const s = stateRef.current
    if (s.phase === 'attack') {
      playPunchSound()
      const dmg = s.isColdBlood ? 20 : 12 // Daño doble en Sangre Fría
      s.enemyHp = Math.max(0, s.enemyHp - dmg)
      s.score += s.isColdBlood ? 120 : 60
      s.punches += 1
      s.cameraShake = 0.28
      setEnemyHp(s.enemyHp)
      setScore(s.score)
      setPunchesCount(s.punches)
      bumpCombo()
      triggerFx(s.isColdBlood ? '¡CRÍTICO!' : '¡PEGÓ!', `+${s.isColdBlood ? 120 : 60}`, s.isColdBlood ? '#30d158' : '#ffd700')
      triggerFlash(s.isColdBlood ? 'rgba(48,209,88,0.26)' : 'rgba(255,215,0,0.2)')
    } else {
      // Intento de golpe durante fase de esquive (guardia rival cerrada)
      playWhooshSound()
    }
  }, [bumpCombo, triggerFx, triggerFlash])

  // Suscripción a trofeos
  useEffect(() => {
    const unsub = subscribeAchievementUnlocked(ach => {
      playTrophySound()
      setUnlockedToast(ach)
      setTimeout(() => setUnlockedToast(null), 3600)
    })
    return unsub
  }, [])

  // Teclado de respaldo
  useEffect(() => {
    const onKeyDown = (e) => {
      const s = stateRef.current
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        s.playerPose = 'left'
        setCurrentPose('left')
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        s.playerPose = 'right'
        setCurrentPose('right')
      } else if (e.code === 'ArrowDown' || e.code === 'KeyS') {
        s.playerPose = 'duck'
        setCurrentPose('duck')
      } else if (e.code === 'ArrowUp' || e.code === 'KeyW') {
        s.playerPose = 'back'
        setCurrentPose('back')
      } else if (e.code === 'Space') {
        executePlayerPunch()
      }
    }
    const onKeyUp = (e) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'KeyA', 'KeyD', 'KeyS', 'KeyW'].includes(e.code)) {
        stateRef.current.playerPose = 'center'
        setCurrentPose('center')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [executePlayerPunch])

  // Iniciar combate
  const startFight = useCallback(() => {
    playBellSound()
    stateRef.current = {
      phase: 'dodge',
      phaseTimer: 15,
      round: 1,
      score: 0,
      slips: 0,
      punches: 0,
      enemyHp: 100,
      playerHp: 100,
      playerPose: 'center',
      attackPending: null,
      attackProgress: 0,
      hasDodgedCurrent: false,
      currentHr: liveHr,
      isColdBlood: liveHr <= 115,
      lastPunchTs: 0,
      prevAccZ: 0,
      cameraShake: 0,
      combo: 0,
      startTime: Date.now(),
    }
    setPhase('dodge')
    setPhaseTimer(15)
    setRound(1)
    setScore(0)
    setEnemyHp(100)
    setPlayerHp(100)
    setSlipsCount(0)
    setPunchesCount(0)
    setCombo(0)
    setFx(null)
    setFlash(null)
    setGameState('playing')
  }, [liveHr])

  // ── MOTOR GRÁFICO 3D EN THREE.JS ──
  useEffect(() => {
    if (gameState !== 'playing') return
    const container = mountRef.current
    if (!container) return

    const width = container.clientWidth || 840
    const height = 440

    // 1. Escena y Cámara 3D
    const scene = new THREE.Scene()
    scene.background = new THREE.Color('#060810')
    scene.fog = new THREE.FogExp2('#060810', 0.035)

    const camera = new THREE.PerspectiveCamera(52, width / height, 0.1, 100)
    camera.position.set(0, 1.6, 3.2) // Vista en primera persona / hombro del boxeador

    let renderer
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' })
    } catch {
      // GPU/navegador sin WebGL disponible (o demasiados contextos abiertos): no reventamos
      // el error boundary; mostramos un aviso y dejamos salir.
      setGlError(true)
      return
    }
    setGlError(false)
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.shadowMap.enabled = true
    // Si el contexto se pierde en caliente (cambio de GPU, suspensión), avisamos sin crashear.
    renderer.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); setGlError(true) })
    container.appendChild(renderer.domElement)

    // 2. Iluminación de Estadio
    const ambientLight = new THREE.AmbientLight('#ffffff', 0.6)
    scene.add(ambientLight)

    // Focos de ring principales (Spotlights)
    const spotBlue = new THREE.SpotLight('#38bdf8', 2.5, 20, Math.PI / 4, 0.3)
    spotBlue.position.set(-3, 6, 2)
    scene.add(spotBlue)

    const spotRed = new THREE.SpotLight('#ef4444', 2.5, 20, Math.PI / 4, 0.3)
    spotRed.position.set(3, 6, 2)
    scene.add(spotRed)

    const centerSpot = new THREE.SpotLight('#ffffff', 3, 20, Math.PI / 5, 0.2)
    centerSpot.position.set(0, 7, 0)
    scene.add(centerSpot)

    // 3. El Cuadrilátero / Ring 3D
    // Lona
    const canvasGeo = new THREE.BoxGeometry(6, 0.4, 6)
    const canvasMat = new THREE.MeshStandardMaterial({ color: '#1e293b', roughness: 0.8 })
    const ringCanvas = new THREE.Mesh(canvasGeo, canvasMat)
    ringCanvas.position.y = -0.2
    scene.add(ringCanvas)

    // 4 Postes de las esquinas
    const postGeo = new THREE.CylinderGeometry(0.08, 0.08, 2.2, 16)
    const postMatRed = new THREE.MeshStandardMaterial({ color: '#dc2626', roughness: 0.3, metalness: 0.4 })
    const postMatBlue = new THREE.MeshStandardMaterial({ color: '#0284c7', roughness: 0.3, metalness: 0.4 })

    const corners = [
      { x: -2.8, z: -2.8, mat: postMatRed },
      { x: 2.8, z: -2.8, mat: postMatBlue },
      { x: -2.8, z: 2.8, mat: postMatBlue },
      { x: 2.8, z: 2.8, mat: postMatRed },
    ]
    corners.forEach(c => {
      const p = new THREE.Mesh(postGeo, c.mat)
      p.position.set(c.x, 1.1, c.z)
      scene.add(p)
    })

    // Cuerdas de boxeo rojas y blancas
    const ropeHeights = [0.6, 1.0, 1.4, 1.8]
    ropeHeights.forEach((h, idx) => {
      const ropeGeo = new THREE.TorusGeometry(3.9, 0.02, 8, 4)
      const ropeMat = new THREE.MeshStandardMaterial({ color: idx % 2 === 0 ? '#ef4444' : '#ffffff', roughness: 0.4 })
      const rope = new THREE.Mesh(ropeGeo, ropeMat)
      rope.rotation.x = Math.PI / 2
      rope.rotation.z = Math.PI / 4
      rope.position.y = h
      scene.add(rope)
    })

    // 4. Multitud en las Tribunas y Flashes de Cámara
    const crowdCount = 120
    const crowdGeo = new THREE.SphereGeometry(0.16, 8, 8)
    const crowdMat = new THREE.MeshBasicMaterial({ color: '#334155' })
    const crowdMesh = new THREE.InstancedMesh(crowdGeo, crowdMat, crowdCount)
    const dummy = new THREE.Object3D()

    for (let i = 0; i < crowdCount; i++) {
      const angle = (i / crowdCount) * Math.PI * 2
      const radius = 5.2 + Math.random() * 2.5
      dummy.position.set(
        Math.cos(angle) * radius,
        0.8 + Math.random() * 3.2,
        Math.sin(angle) * radius
      )
      dummy.updateMatrix()
      crowdMesh.setMatrixAt(i, dummy.matrix)
    }
    scene.add(crowdMesh)

    // Flashes de fotógrafos
    const flashLight = new THREE.PointLight('#ffffff', 0, 15)
    scene.add(flashLight)

    // 5. El Rival 3D (Shadow Boxer con Guantes de Cuero)
    const rivalGroup = new THREE.Group()

    // Cabeza
    const headGeo = new THREE.SphereGeometry(0.24, 20, 20)
    const headMat = new THREE.MeshStandardMaterial({ color: '#475569', roughness: 0.5 })
    const rivalHead = new THREE.Mesh(headGeo, headMat)
    rivalHead.position.y = 1.65
    rivalGroup.add(rivalHead)

    // Torso
    const torsoGeo = new THREE.CylinderGeometry(0.38, 0.28, 0.7, 16)
    const torsoMat = new THREE.MeshStandardMaterial({ color: '#334155', roughness: 0.6 })
    const rivalTorso = new THREE.Mesh(torsoGeo, torsoMat)
    rivalTorso.position.y = 1.15
    rivalGroup.add(rivalTorso)

    // Guantes de Boxeo 3D
    const gloveGeo = new THREE.SphereGeometry(0.18, 16, 16)
    const gloveMat = new THREE.MeshStandardMaterial({ color: '#dc2626', roughness: 0.25, metalness: 0.2 })
    const gloveL = new THREE.Mesh(gloveGeo, gloveMat)
    const gloveR = new THREE.Mesh(gloveGeo, gloveMat)
    gloveL.position.set(-0.45, 1.45, 0.4)
    gloveR.position.set(0.45, 1.45, 0.4)
    rivalGroup.add(gloveL)
    rivalGroup.add(gloveR)

    rivalGroup.position.set(0, 0, -0.4)
    scene.add(rivalGroup)

    // ── Bucle de Renderizado 3D y Lógica del Combate ──
    let animId = null
    let clock = new THREE.Clock()
    let attackCooldown = 0
    let flashCooldown = 0

    const animate = () => {
      const dt = Math.min(0.05, clock.getDelta())
      const elapsed = clock.getElapsedTime()
      const s = stateRef.current

      // 1. Temporizador de Fases: Esquive (15s) vs Pegar (8s)
      s.phaseTimer -= dt
      setPhaseTimer(Math.max(0, Math.ceil(s.phaseTimer)))

      if (s.phaseTimer <= 0) {
        if (s.phase === 'dodge') {
          // ¡CAMBIO A FASE DE ATAQUE (HORA DE PEGAR)!
          playBellSound()
          s.phase = 'attack'
          s.phaseTimer = 8 // 8 segundos para noquear al rival
          s.attackPending = null
          setPhase('attack')
        } else {
          // ¡VUELTA A FASE DE ESQUIVE!
          playBellSound()
          s.phase = 'dodge'
          s.phaseTimer = 15 // 15 segundos esquivando
          setPhase('dodge')
        }
      }

      // 2. Comportamiento del Rival según la Fase Activa
      if (s.phase === 'dodge') {
        // En Fase Esquive: El rival ataca en ráfagas
        rivalHead.material.color.set('#475569')
        gloveMat.color.set('#dc2626')

        attackCooldown -= dt
        if (!s.attackPending && attackCooldown <= 0) {
          const attacks = ['left_hook', 'right_hook', 'jab', 'pull', 'footwork']
          s.attackPending = attacks[Math.floor(Math.random() * attacks.length)]
          s.attackProgress = 0
          s.hasDodgedCurrent = false
          if (s.attackPending === 'footwork') {
            s.hopsNeeded = 3
            s.hopsDone = 0
            setHopsCount(0)
            setFootworkPrompt('¡SALTÁ 3 VECES EN EL LUGAR!')
            attackCooldown = 3.6 // 3.6s para rebotar
          } else {
            setFootworkPrompt(null)
            attackCooldown = s.isColdBlood ? 2.2 : 1.6 // Más tiempo para esquivar si estás en calma
          }
        }

        if (s.attackPending) {
          s.attackProgress += dt * (s.isColdBlood ? 1.4 : 1.9)
          const p = s.attackProgress

          // Movimiento del golpe hacia la cámara
          if (s.attackPending === 'left_hook') {
            gloveL.position.x = -0.45 + Math.sin(p * Math.PI) * 0.9
            gloveL.position.z = 0.4 + Math.sin(p * Math.PI) * 1.5
            gloveL.position.y = 1.45 + Math.sin(p * Math.PI) * 0.2
          } else if (s.attackPending === 'right_hook') {
            gloveR.position.x = 0.45 - Math.sin(p * Math.PI) * 0.9
            gloveR.position.z = 0.4 + Math.sin(p * Math.PI) * 1.5
            gloveR.position.y = 1.45 + Math.sin(p * Math.PI) * 0.2
          } else if (s.attackPending === 'jab') {
            gloveL.position.z = 0.4 + Math.sin(p * Math.PI) * 1.7
            gloveL.position.y = 1.55 + Math.sin(p * Math.PI) * 0.1
          } else if (s.attackPending === 'pull') {
            gloveR.position.z = 0.4 + Math.sin(p * Math.PI) * 1.6
          } else if (s.attackPending === 'footwork') {
            // El rival hace repiqueteo y amagues rápidos con los pies
            rivalGroup.position.x = Math.sin(p * Math.PI * 4) * 0.4
            gloveL.position.y = 1.45 + Math.sin(p * Math.PI * 6) * 0.08
            gloveR.position.y = 1.45 + Math.cos(p * Math.PI * 6) * 0.08
          }

          // Chequear esquive en el momento de máximo impacto
          if (p >= 0.65 && !s.hasDodgedCurrent) {
            if (s.attackPending === 'footwork') {
              if (s.hopsDone >= s.hopsNeeded) {
                // Completó los 3 saltitos
                s.hasDodgedCurrent = true
                s.slips += 1
                s.score += 150
                setSlipsCount(s.slips)
                setScore(s.score)
                setFootworkPrompt(null)
                bumpCombo()
                triggerFx('¡FOOTWORK!', '+150', '#30d158')
              } else if (p >= 0.92) {
                // No saltó lo suficiente -> descuido de guardia
                playHitSound()
                s.hasDodgedCurrent = true
                s.playerHp = Math.max(0, s.playerHp - 16)
                s.cameraShake = 0.25
                setPlayerHp(s.playerHp)
                setFootworkPrompt(null)
                resetCombo()
                triggerFx('¡TE PEGÓ!', '-16', '#ff453a')
                triggerFlash('rgba(255,59,48,0.32)')
                if (s.playerHp <= 0) {
                  setGameState('gameover')
                  return
                }
              }
            } else {
              let requiredDodge = 'right'
              if (s.attackPending === 'left_hook') requiredDodge = 'right'
              if (s.attackPending === 'right_hook') requiredDodge = 'left'
              if (s.attackPending === 'jab') requiredDodge = 'duck'
              if (s.attackPending === 'pull') requiredDodge = 'back'

              if (s.playerPose === requiredDodge) {
                // ¡Esquive limpio con el torso!
                playWhooshSound()
                s.hasDodgedCurrent = true
                s.slips += 1
                s.score += 60
                setSlipsCount(s.slips)
                setScore(s.score)
                bumpCombo()
                triggerFx('¡ESQUIVÓ!', '+60', '#64d2ff')
              } else if (p >= 0.85) {
                // Impacto recibido
                playHitSound()
                s.hasDodgedCurrent = true
                s.playerHp = Math.max(0, s.playerHp - 16)
                s.cameraShake = 0.25
                setPlayerHp(s.playerHp)
                resetCombo()
                triggerFx('¡TE PEGÓ!', '-16', '#ff453a')
                triggerFlash('rgba(255,59,48,0.32)')

                if (s.playerHp <= 0) {
                  // Game Over
                  setGameState('gameover')
                  return
                }
              }
            }
          }

          if (p >= 1.0) {
            // Golpe terminado, volver a guardia
            gloveL.position.set(-0.45, 1.45, 0.4)
            gloveR.position.set(0.45, 1.45, 0.4)
            rivalGroup.position.x = 0
            s.attackPending = null
            setFootworkPrompt(null)
          }
        } else {
          // Bouncing sutil de guardia
          gloveL.position.y = 1.45 + Math.sin(elapsed * 4) * 0.04
          gloveR.position.y = 1.45 + Math.cos(elapsed * 4) * 0.04
          rivalHead.position.y = 1.65 + Math.sin(elapsed * 3) * 0.03
        }
      } else {
        // ── EN FASE DE ATAQUE: Rival vulnerable y atontado ──
        rivalHead.material.color.set('#38bdf8')
        gloveMat.color.set('#0284c7')

        // Rival baja la guardia tambaleándose
        gloveL.position.set(-0.55, 1.05, 0.2)
        gloveR.position.set(0.55, 1.05, 0.2)
        rivalHead.rotation.z = Math.sin(elapsed * 6) * 0.2
        rivalGroup.position.y = Math.sin(elapsed * 5) * 0.05

        // Focos de ring en modo éxtasis dorado
        centerSpot.color.set(s.isColdBlood ? '#ffd700' : '#ffffff')
      }

      // 3. Flashes de cámara en las tribunas
      flashCooldown -= dt
      if (flashCooldown <= 0) {
        flashCooldown = 0.15 + Math.random() * 0.4
        flashLight.intensity = Math.random() > 0.3 ? 8 : 0
        const randomAngle = Math.random() * Math.PI * 2
        flashLight.position.set(Math.cos(randomAngle) * 5.5, 1.8 + Math.random() * 2, Math.sin(randomAngle) * 5.5)
      } else {
        flashLight.intensity = Math.max(0, flashLight.intensity - 25 * dt)
      }

      // 4. Inclinación de la Cámara 3D según el torso del jugador
      let camTargetX = 0
      let camTargetY = 1.6
      if (s.playerPose === 'left')  camTargetX = -0.55 // Inclinado izquierda
      if (s.playerPose === 'right') camTargetX = 0.55  // Inclinado derecha
      if (s.playerPose === 'duck')  camTargetY = 1.15  // Agachado
      if (s.playerPose === 'back')  camTargetY = 1.85  // Tirado atrás

      camera.position.x += (camTargetX - camera.position.x) * 12 * dt
      camera.position.y += (camTargetY - camera.position.y) * 12 * dt

      // Shake de impacto
      if (s.cameraShake > 0) {
        camera.position.x += (Math.random() - 0.5) * s.cameraShake
        camera.position.y += (Math.random() - 0.5) * s.cameraShake
        s.cameraShake = Math.max(0, s.cameraShake - 1.2 * dt)
      }
      camera.lookAt(0, 1.45, -0.4)

      // Chequear si el rival fue noqueado
      if (s.enemyHp <= 0) {
        playBellSound()
        const durationSec = Math.round((Date.now() - s.startTime) / 1000)
        setLastStats({
          score: s.score + 500,
          slips: s.slips,
          punches: s.punches,
          durationSec,
          isKO: true,
          isConnected: connectedRef.current
        })
        evaluateGameStats({
          score: s.score + 500,
          durationSec,
          calmSec: s.isColdBlood ? 30 : 0,
          orbs: s.slips,
          hits: 100 - s.playerHp,
          isConnected: connectedRef.current,
          maxRmssd: 60
        })
        setGameState('gameover')
        return
      }

      renderer.render(scene, camera)
      animId = requestAnimationFrame(animate)
    }

    animId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animId)
      // Liberar la memoria de geometrías/materiales/texturas de la escena.
      scene.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose()
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
          mats.forEach(m => {
            for (const k in m) { const v = m[k]; if (v && v.isTexture) v.dispose() }
            m.dispose()
          })
        }
      })
      renderer.dispose()
      // CLAVE: dispose() no suelta el contexto WebGL. Sin esto, cada entrada/salida del
      // juego deja un contexto vivo y a los ~16 el navegador tira "Error creating WebGL context".
      renderer.forceContextLoss()
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement)
      }
    }
  }, [gameState])

  return (
    <div style={{
      position: 'relative', width: '100%', maxWidth: 840, margin: '0 auto',
      background: '#060810', borderRadius: 18, border: '1px solid rgba(255,255,255,0.12)',
      overflow: 'hidden', boxShadow: '0 8px 36px rgba(0,0,0,0.5)',
      userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
    }}>
      <style>{`
        @keyframes fxPop { 0%{opacity:0;transform:translate(-50%,-50%) scale(0.55)} 18%{opacity:1;transform:translate(-50%,-50%) scale(1.18)} 42%{transform:translate(-50%,-50%) scale(1)} 100%{opacity:0;transform:translate(-50%,-78%) scale(1)} }
        @keyframes flashFade { 0%{opacity:1} 100%{opacity:0} }
      `}</style>
      {/* ── 1. HUD Superior del Ring 3D ── */}
      <div style={{
        padding: '10px 16px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10
      }}>
        {/* Pulso y Sangre Fría */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 9, height: 9, borderRadius: '50%',
            background: liveHr <= 115 ? '#30d158' : '#ef4444',
            boxShadow: `0 0 8px ${liveHr <= 115 ? '#30d158' : '#ef4444'}`,
          }} />
          <span style={{ fontSize: 13, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {liveHr} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>BPM</span>
          </span>
          <span style={{
            fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 6,
            background: liveHr <= 115 ? 'rgba(48,209,88,0.22)' : 'rgba(239,68,68,0.22)',
            color: liveHr <= 115 ? '#30d158' : '#ef4444'
          }}>
            {liveHr <= 115 ? 'SANGRE FRÍA (DAÑO x2)' : 'PULSO ALTO'}
          </span>
        </div>

        {/* Indicador de Fase Gigante: ESQUIVAR vs PEGAR */}
        <div style={{
          background: phase === 'attack' ? 'rgba(255,215,0,0.18)' : 'rgba(0,122,255,0.18)',
          border: `1.5px solid ${phase === 'attack' ? '#ffd700' : '#007aff'}`,
          borderRadius: 20, padding: '4px 14px', display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span style={{ fontSize: 11, fontWeight: 900, color: phase === 'attack' ? '#ffd700' : '#64d2ff', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {phase === 'attack' ? '¡¡PEGÁ AHORA!!' : 'ESQUIVÁ LOS GOLPES'}
          </span>
          <span style={{ fontSize: 12, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {phaseTimer}s
          </span>
        </div>

        {/* Barras de Vida y Puntos */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 80 }}>
            <span style={{ fontSize: 8.5, fontWeight: 800, color: '#ef4444', textTransform: 'uppercase' }}>Rival {enemyHp}%</span>
            <div style={{ height: 5, width: 80, background: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${enemyHp}%`, height: '100%', background: '#ef4444', transition: 'width 0.2s ease' }} />
            </div>
          </div>
          {combo >= 2 && (
            <span style={{ fontSize: 11, fontWeight: 900, color: '#ff9f0a', background: 'rgba(255,159,10,0.18)', border: '1px solid rgba(255,159,10,0.4)', padding: '2px 8px', borderRadius: 8, fontVariantNumeric: 'tabular-nums', animation: 'fadeIn 0.2s ease' }}>
              COMBO ×{combo}
            </span>
          )}
          <span style={{ fontSize: 16, fontWeight: 900, color: '#ffd700', fontVariantNumeric: 'tabular-nums' }}>
            {score} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>PTS</span>
          </span>
          <button
            onClick={onExit}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
              color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 700,
              padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            ✕ Salir
          </button>
        </div>
      </div>

      {/* ── 2. Canvas 3D de Three.js (Ring + Estadio + Flashes) ── */}
      <div style={{ position: 'relative', width: '100%', height: 440 }}>
        <div ref={mountRef} style={{ width: '100%', height: '100%' }} />

        {/* Flash de pantalla al pegar (dorado/verde) o recibir (rojo) */}
        {flash && (
          <div key={flash.id} style={{ position: 'absolute', inset: 0, background: flash.color, zIndex: 33, pointerEvents: 'none', animation: 'flashFade 0.17s ease-out forwards' }} />
        )}

        {/* Texto de impacto grande: ¡PEGÓ! / ¡ESQUIVÓ! / ¡TE PEGÓ! con su puntaje */}
        {fx && (
          <div key={fx.id} style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', zIndex: 36, pointerEvents: 'none', animation: 'fxPop 0.72s ease-out forwards' }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: fx.color, textShadow: `0 2px 20px ${fx.color}, 0 1px 3px rgba(0,0,0,0.5)`, letterSpacing: -0.5 }}>{fx.text}</div>
            {fx.sub && <div style={{ fontSize: 19, fontWeight: 900, color: '#fff', textShadow: '0 2px 10px rgba(0,0,0,0.65)' }}>{fx.sub}</div>}
          </div>
        )}

        {/* Fallback si el navegador/GPU no pudo abrir WebGL (en vez de reventar el error boundary) */}
        {glError && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.96) 0%, rgba(6,8,14,0.99) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 40
          }}>
            <span style={{ fontSize: 30, marginBottom: 8 }}>🥊</span>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>No se pudo abrir el ring 3D</h3>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', maxWidth: 380, lineHeight: 1.5, margin: '0 0 16px' }}>
              Tu navegador no pudo crear el gráfico 3D (WebGL). Suele pasar si hay muchas pestañas o ventanas 3D abiertas, o si la aceleración por hardware está desactivada. Recargá la página para liberar memoria de video.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => window.location.reload()} style={{ background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Recargar
              </button>
              <button onClick={onExit} style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Salir
              </button>
            </div>
          </div>
        )}

        {/* Cartel Gigante de Saltitos de Boxeo / Footwork Activo */}
        {phase === 'dodge' && footworkPrompt && (
          <div style={{
            position: 'absolute', top: 40, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(15, 20, 32, 0.94)', border: '2px solid #30d158',
            borderRadius: 14, padding: '10px 22px', boxShadow: '0 0 28px rgba(48,209,88,0.4)',
            textAlign: 'center', zIndex: 12, animation: 'pulse 0.8s ease-in-out infinite'
          }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#30d158', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block' }}>
              ¡¡FOOTWORK / SALTITOS DE BOXEO!!
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#ffffff' }}>
              Rebotá en punta de pie: <strong style={{ color: '#ffd700', fontSize: 15 }}>{hopsCount} / 3</strong>
            </span>
          </div>
        )}

        {/* Radar de Postura en Vivo del Acelerómetro */}
        <div style={{
          position: 'absolute', bottom: 14, left: 16,
          background: 'rgba(15, 20, 32, 0.88)', border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 10, padding: '6px 12px', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', gap: 7, zIndex: 10
        }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Torso:</span>
          <span style={{
            fontSize: 11, fontWeight: 900,
            color: currentPose === 'center' ? 'var(--text-soft)' : '#30d158'
          }}>
            {currentPose === 'left' ? '← Inclinado Izquierda' : currentPose === 'right' ? 'Inclinado Derecha →' : currentPose === 'duck' ? '↓ Agachado (Duck)' : currentPose === 'back' ? '↑ Tirado Atrás' : currentPose === 'hop' ? '👟 ¡SALTITO EN PUNTA DE PIE!' : currentPose === 'punch' ? '🥊 ¡GOLPE SECO!' : '● Guardia Central'}
          </span>
        </div>

        {/* Notificación Toast de Trofeo */}
        {unlockedToast && (
          <div style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(15, 20, 32, 0.92)', border: '1.5px solid #ffd700',
            borderRadius: 12, padding: '8px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'fadeIn 0.25s ease', backdropFilter: 'blur(12px)', zIndex: 30
          }}>
            <span style={{ fontSize: 16 }}>★</span>
            <div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#ffd700', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block' }}>
                ¡Trofeo Desbloqueado!
              </span>
              <span style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
                {unlockedToast.title}
              </span>
            </div>
          </div>
        )}

        {/* ── Pantalla de Inicio / Reglas de las 2 Fases ── */}
        {gameState === 'menu' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.95) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <span style={{ fontSize: 40, marginBottom: 6, filter: 'drop-shadow(0 4px 14px rgba(255,59,48,0.5))' }}>🥊</span>
            <h2 style={{ fontSize: 25, fontWeight: 800, color: '#fff', margin: '0 0 4px', letterSpacing: -0.5 }}>
              Polar Boxing 3D
            </h2>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', margin: '0 0 18px' }}>
              Sombra y ring · tu pulso y tu cuerpo son el control
            </p>
            <div style={{ maxWidth: 470, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
              <div style={{ background: 'rgba(0,122,255,0.12)', borderLeft: '3px solid #007aff', padding: '9px 13px', borderRadius: '4px 10px 10px 4px' }}>
                <strong style={{ color: '#64d2ff', fontSize: 12, display: 'block', marginBottom: 2 }}>1 · Esquivá · 15 s</strong>
                <span style={{ fontSize: 11.5, color: '#e2e8f0', lineHeight: 1.45 }}>El rival ataca. Incliná el torso a izquierda/derecha, agachate ante los jabs y tirate atrás.</span>
              </div>
              <div style={{ background: 'rgba(255,215,0,0.12)', borderLeft: '3px solid #ffd700', padding: '9px 13px', borderRadius: '4px 10px 10px 4px' }}>
                <strong style={{ color: '#ffd700', fontSize: 12, display: 'block', marginBottom: 2 }}>2 · Pegá · 8 s</strong>
                <span style={{ fontSize: 11.5, color: '#e2e8f0', lineHeight: 1.45 }}>Suena la campana y el rival baja la guardia: meté golpes secos hacia adelante con el pecho para noquearlo. Con el pulso en calma (menos de 115) el daño es doble.</span>
              </div>
            </div>
            {/* Cómo se juega: con sensor o con teclado */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 20, maxWidth: 470 }}>
              <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.78)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px' }}>
                📡 Con el H10 movés el torso de verdad
              </span>
              <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.78)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px' }}>
                ⌨️ Sin sensor: flechas para esquivar · espacio para pegar
              </span>
            </div>
            <button
              onClick={startFight}
              style={{
                background: '#ff3b30', color: '#fff', border: 'none',
                borderRadius: 12, padding: '13px 34px', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 22px rgba(255,59,48,0.5)',
                letterSpacing: 0.2
              }}
            >
              Subir al ring
            </button>
          </div>
        )}

        {/* ── Game Over / Resultados ── */}
        {gameState === 'gameover' && lastStats && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(20,12,14,0.95) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <h2 style={{ fontSize: 28, fontWeight: 900, color: lastStats.isKO ? '#30d158' : '#fff', margin: '0 0 8px' }}>
              {lastStats.isKO ? '¡KNOCKOUT VICTORIOSO EN EL RING!' : 'Fin del Combate'}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, width: '100%', maxWidth: 380, marginBottom: 18 }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Puntos</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#ffd700' }}>{lastStats.score}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Esquives</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#64d2ff' }}>{lastStats.slips}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Golpes dados</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#30d158' }}>{lastStats.punches}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={startFight}
                style={{
                  background: '#ef4444', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Pelear de Nuevo
              </button>
              <button
                onClick={() => setGameState('menu')}
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Menú
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Instrucción al pie ── */}
      <div style={{
        padding: '8px 16px', background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.5)'
      }}>
        <span>Acelerómetro Polar H10: Inclinación lateral, agacharse y golpe seco frontal con el esternón</span>
        <span>Teclado: Flechas para esquivar · Espacio para pegar</span>
      </div>
    </div>
  )
}
